import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Home, CheckCircle, Shield, Clock, Star, LifeBuoy, Mail, Phone, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showSupport, setShowSupport] = useState(false);
  const [siteReviews, setSiteReviews] = useState([]);
  const [footer, setFooter] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-reviews?limit=12`).then((r) => setSiteReviews(r.data)).catch(() => {});
    axios.get(`${API}/footer`).then((r) => setFooter(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then((r) => setSite(r.data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (user && user !== false) {
      if (user.role === 'admin') navigate('/admin');
      else navigate('/dashboard');
    }
  }, [user, navigate]);

  const s = (k, fb = '') => (site && site[k] != null && site[k] !== '') ? site[k] : fb;
  const accent = s('accent_color', '#EA580C');
  const dark = s('dark_color', '#0F172A');
  const heroImg = s('hero_image_url') ? (s('hero_image_url').startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${s('hero_image_url')}` : s('hero_image_url')) : '';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-8 h-8" style={{ color: accent }} />
            <span className="text-2xl font-bold" style={{ color: dark }}>{s('brand_name', 'RISTRUTTURA CASA ROMA')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="header-support-btn"
              onClick={() => setShowSupport(true)}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-4"
            >
              <LifeBuoy className="w-4 h-4 mr-2" /> Supporto
            </Button>
            <Button
              data-testid="header-login-btn"
              onClick={() => { setIsLogin(true); setShowAuth(true); }}
              className="text-white rounded-lg px-6"
              style={{ backgroundColor: accent }}
            >
              Accedi
            </Button>
          </div>
        </div>
      </header>

      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6" style={{ color: dark }}>
              {s('hero_title', 'Ristruttura il tuo appartamento a Roma')}
            </h1>
            <p className="text-lg leading-relaxed text-slate-600 mb-8">
              {s('hero_subtitle', footer?.tagline || 'Metti in contatto proprietari e imprese certificate per progetti di ristrutturazione di qualità nella capitale.')}
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button
                data-testid="hero-proprietario-btn"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                className="text-white rounded-lg px-8 py-6 text-lg"
                style={{ backgroundColor: accent }}
              >
                {s('hero_cta_proprietario', "Sono un Proprietario")}
              </Button>
              <Button
                data-testid="hero-impresa-btn"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                variant="outline"
                className="hover:bg-[#F1F5F9] rounded-lg px-8 py-6 text-lg"
                style={{ borderColor: dark, color: dark }}
              >
                {s('hero_cta_impresa', "Sono un'Impresa")}
              </Button>
            </div>
          </div>
          <div className="relative">
            {heroImg && (
              <img
                src={heroImg}
                alt="Modern Rome apartment renovation"
                className="rounded-xl shadow-md w-full"
                data-testid="hero-image"
              />
            )}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">{s('how_it_works_eyebrow', 'Come Funziona')}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" style={{ color: dark }}>
              {s('how_it_works_title', 'Semplice, Sicuro, Professionale')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { key: 'step1', icon: Home, title: s('step1_title', '1. Pubblica Richiesta'), desc: s('step1_description', 'I proprietari pubblicano dettagli del progetto di ristrutturazione con foto e budget.') },
              { key: 'step2', icon: Building2, title: s('step2_title', '2. Ricevi Preventivi'), desc: s('step2_description', 'Le imprese certificate inviano preventivi dettagliati per il tuo progetto.') },
              { key: 'step3', icon: CheckCircle, title: s('step3_title', '3. Scegli e Paga'), desc: s('step3_description', 'Confronta i preventivi, scegli il migliore e procedi con pagamento sicuro.') },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.key} className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${accent}1A` }}>
                    <Icon className="w-6 h-6" style={{ color: accent }} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-medium tracking-tight mb-4" style={{ color: dark }}>{step.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-6" style={{ background: `linear-gradient(135deg, ${accent}10 0%, ${accent}05 100%)` }} data-testid="consultation-promo-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full" style={{ color: accent, backgroundColor: `${accent}1A` }}>{s('consult_promo_eyebrow', 'Servizio dedicato')}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-4" style={{ color: dark }}>
                {s('consult_promo_title', 'Hai dubbi sul tuo progetto? Parla con un esperto al telefono')}
              </h2>
              <p className="text-base leading-relaxed text-slate-700 mb-6">
                {s('consult_promo_description', "Prenota una consulenza telefonica personalizzata con i nostri specialisti di ristrutturazione a Roma. Un'occasione per chiarire ogni dubbio, valutare costi, capire iter burocratici e ricevere consigli pratici prima di affidarti a un'impresa.")}
              </p>
              <ul className="space-y-2 mb-8 text-sm text-slate-700">
                {['consult_promo_bullet1', 'consult_promo_bullet2', 'consult_promo_bullet3', 'consult_promo_bullet4'].map((k, idx) => {
                  const fallback = [
                    'Risposte chiare su preventivi, materiali e tempistiche',
                    'Pagamento sicuro tramite bonifico — nessuna sorpresa',
                    'Confidenzialità garantita: nessun dato condiviso con terzi',
                    'Esperti del mercato romano, conoscono normative e fornitori locali',
                  ][idx];
                  const txt = s(k, fallback);
                  if (!txt) return null;
                  return (
                    <li key={k} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} /> {txt}</li>
                  );
                })}
              </ul>
              <Button
                data-testid="consultation-cta-btn"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                className="text-white rounded-lg px-8 py-6 text-base"
                style={{ backgroundColor: accent }}
              >
                <Phone className="w-5 h-5 mr-2" /> {s('consult_promo_cta', 'Prenota la tua consulenza')}
              </Button>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-testid="consultation-promo-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
                    <Phone className="w-6 h-6" style={{ color: accent }} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Consulenza telefonica</p>
                    <p className="font-semibold" style={{ color: dark }}>{s('consult_card_title', 'Confronto 1-a-1 con un esperto')}</p>
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-4 space-y-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between"><span>Durata media</span><strong style={{ color: dark }}>{s('consult_card_duration', '30 minuti')}</strong></div>
                  <div className="flex items-center justify-between"><span>Pagamento</span><strong style={{ color: dark }}>{s('consult_card_payment', 'Bonifico bancario')}</strong></div>
                  <div className="flex items-center justify-between"><span>Disponibilità</span><strong style={{ color: dark }}>{s('consult_card_availability', 'Lun-Ven 9:00-18:00')}</strong></div>
                  <div className="flex items-center justify-between"><span>Lingua</span><strong style={{ color: dark }}>{s('consult_card_language', 'Italiano')}</strong></div>
                </div>
                <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: `${accent}10` }}>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    💡 <strong>Come funziona:</strong> {s('consult_card_howto', "registrati come Proprietario, descrivi il tuo problema, ricevi via email le coordinate del bonifico. Dopo l'accredito ti chiamiamo entro 24h lavorative.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">{s('reviews_eyebrow', 'Le voci dei nostri clienti')}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" style={{ color: dark }}>{s('reviews_title', 'Cosa dicono di noi')}</h2>
          </div>
          {siteReviews.length === 0 ? (
            <p className="text-center text-slate-500">Le prime recensioni arriveranno presto.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="landing-reviews">
              {siteReviews.slice(0, 6).map((r) => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all" data-testid={`landing-review-${r.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className="w-4 h-4" style={{ color: n <= r.rating ? accent : '#cbd5e1', fill: n <= r.rating ? accent : 'transparent' }} />
                      ))}
                    </div>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {r.tipo === 'consultation' ? 'Consulenza' : 'Piattaforma'}
                    </span>
                  </div>
                  <p className="text-slate-700 mb-3 italic">"{r.commento}"</p>
                  <p className="text-sm font-medium" style={{ color: dark }}>— {r.proprietario_name || 'Cliente'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 px-6 text-white" style={{ backgroundColor: dark }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-6">{s('cta_title', 'Inizia il tuo progetto oggi')}</h2>
          <p className="text-lg leading-relaxed text-slate-300 mb-8">{s('cta_subtitle', 'Unisciti a centinaia di proprietari e imprese che hanno già trovato il partner perfetto per i loro progetti.')}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              data-testid="cta-inizia-btn"
              onClick={() => { setIsLogin(false); setShowAuth(true); }}
              className="text-white rounded-lg px-8 py-6 text-lg"
              style={{ backgroundColor: accent }}
            >
              {s('cta_button_primary', 'Inizia Ora')}
            </Button>
            <Button
              data-testid="cta-supporto-btn"
              onClick={() => setShowSupport(true)}
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-lg px-8 py-6 text-lg"
            >
              <LifeBuoy className="w-4 h-4 mr-2" /> {s('cta_button_support', 'Richiedi supporto')}
            </Button>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-300 py-12 px-6" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-6 h-6" style={{ color: accent }} />
                <span className="text-xl font-bold text-white">{s('brand_name', 'RISTRUTTURA CASA ROMA')}</span>
              </div>
              <p className="text-sm text-slate-400">{footer?.tagline || s('hero_subtitle', 'Connettiamo proprietari e imprese di ristrutturazione a Roma')}</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">Dati Aziendali</h4>
              <div className="space-y-1 text-xs text-slate-400">
                {footer?.intestazione && <p data-testid="footer-intestazione">{footer.intestazione}</p>}
                {footer?.partita_iva && <p>P.IVA: <span data-testid="footer-piva">{footer.partita_iva}</span></p>}
                {footer?.codice_fiscale && <p>CF: {footer.codice_fiscale}</p>}
                {footer?.sede_legale && <p className="flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> {footer.sede_legale}</p>}
                {footer?.pec && <p>PEC: {footer.pec}</p>}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3">Contatti</h4>
              <div className="space-y-1 text-xs text-slate-400">
                {footer?.email_contatto && <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> <a href={`mailto:${footer.email_contatto}`} className="hover:text-white" data-testid="footer-email">{footer.email_contatto}</a></p>}
                {footer?.telefono && <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> <a href={`tel:${footer.telefono}`} className="hover:text-white">{footer.telefono}</a></p>}
                {footer?.iban && <p className="break-all">IBAN: {footer.iban}</p>}
                <div className="pt-2 flex gap-3">
                  {footer?.privacy_url && <a href={footer.privacy_url} target="_blank" rel="noreferrer" className="hover:text-white underline">Privacy</a>}
                  {footer?.terms_url && <a href={footer.terms_url} target="_blank" rel="noreferrer" className="hover:text-white underline">Termini</a>}
                  <button type="button" onClick={() => setShowSupport(true)} className="hover:text-white underline" data-testid="footer-support-link">Supporto</button>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} {footer?.intestazione || 'RISTRUTTURA CASA ROMA'}. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} isLogin={isLogin} setIsLogin={setIsLogin} />
      <SupportModal open={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
};

const AuthModal = ({ open, onClose, isLogin, setIsLogin }) => {
  const { login, registerProprietario, registerImpresa, verifyOtp, resendOtp } = useAuth();
  const [step, setStep] = useState('main'); // main | role | proprietario | impresa | otp
  const [pendingEmail, setPendingEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [cognome, setCognome] = useState('');
  const [phone, setPhone] = useState('');
  const [ragioneSociale, setRagioneSociale] = useState('');
  const [referente, setReferente] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [sedeLegale, setSedeLegale] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!open) {
      setStep(isLogin ? 'main' : 'role');
      setEmail(''); setPassword(''); setName(''); setCognome(''); setPhone('');
      setRagioneSociale(''); setReferente(''); setPartitaIva(''); setCodiceFiscale(''); setSedeLegale('');
      setPendingEmail(''); setOtpCode('');
    }
  }, [open, isLogin]);

  React.useEffect(() => { if (step !== 'otp') setStep(isLogin ? 'main' : 'role'); /* eslint-disable-next-line */ }, [isLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      toast.success('Accesso effettuato!');
      onClose();
      navigate(result.data?.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      // If unverified, redirect to OTP step
      if (typeof result.error === 'string' && result.error.toLowerCase().includes('non verificata')) {
        setPendingEmail(email);
        setStep('otp');
        toast.info('Ti abbiamo inviato un nuovo codice via email');
      } else {
        toast.error(result.error);
      }
    }
    setLoading(false);
  };

  const handleRegisterProp = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await registerProprietario({ email, password, name, cognome, phone });
    if (result.success) {
      setPendingEmail(email);
      setStep('otp');
      toast.success('Codice di verifica inviato. Controlla la tua email.');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleRegisterImpresa = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await registerImpresa({
      email, password,
      ragione_sociale: ragioneSociale,
      referente, partita_iva: partitaIva, codice_fiscale: codiceFiscale, sede_legale: sedeLegale, phone,
    });
    if (result.success) {
      setPendingEmail(email);
      setStep('otp');
      toast.success('Codice di verifica inviato. Controlla la tua email.');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) { toast.error('Inserisci un codice a 6 cifre'); return; }
    setLoading(true);
    const result = await verifyOtp(pendingEmail, otpCode);
    if (result.success) {
      toast.success('Email verificata. Benvenuto!');
      onClose();
      navigate(result.data?.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    if (!pendingEmail) return;
    setLoading(true);
    const res = await resendOtp(pendingEmail);
    if (res.success) toast.success('Nuovo codice inviato'); else toast.error(res.error);
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">
            {step === 'otp' ? 'Verifica Email' : (isLogin ? 'Accedi' : (step === 'role' ? 'Registrati' : `Registrazione ${step}`))}
          </DialogTitle>
        </DialogHeader>

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="space-y-4 mt-4" data-testid="otp-form">
            <p className="text-sm text-slate-600">
              Ti abbiamo inviato un codice a 6 cifre a <strong className="text-[#0F172A]">{pendingEmail}</strong>.
              Controlla la posta (anche spam) e inserisci il codice qui sotto. Il codice scade tra 15 minuti.
            </p>
            <div>
              <Label>Codice a 6 cifre</Label>
              <Input
                data-testid="otp-input"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                inputMode="numeric"
                pattern="[0-9]*"
                autoFocus
                className="mt-1 rounded-lg text-center text-2xl tracking-[0.4em] font-mono"
              />
            </div>
            <Button type="submit" data-testid="otp-submit-btn" disabled={loading || otpCode.length !== 6} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg py-3">
              {loading ? 'Verifica...' : 'Verifica codice'}
            </Button>
            <div className="text-center text-sm space-y-1">
              <button type="button" data-testid="otp-resend-btn" onClick={handleResendOtp} disabled={loading} className="text-[#EA580C] font-medium hover:underline">
                Non hai ricevuto il codice? Invia di nuovo
              </button>
            </div>
          </form>
        )}

        {step !== 'otp' && isLogin && (
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-lg" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-lg" />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg py-3">
              {loading ? 'Caricamento...' : 'Accedi'}
            </Button>
            <div className="text-center text-sm text-slate-600 space-y-1">
              <p>
                Non hai un account?{' '}
                <button type="button" data-testid="toggle-auth-mode-btn" onClick={() => setIsLogin(false)} className="text-[#EA580C] font-medium hover:underline">Registrati</button>
              </p>
              <p>
                <a href="/forgot-password" data-testid="forgot-password-link" className="text-slate-500 hover:text-[#EA580C] hover:underline">Password dimenticata?</a>
              </p>
            </div>
          </form>
        )}

        {step !== 'otp' && !isLogin && step === 'role' && (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">Che tipo di account vuoi creare?</p>
            <button type="button" data-testid="select-proprietario-btn" onClick={() => setStep('proprietario')} className="w-full border-2 border-slate-200 hover:border-[#EA580C] rounded-lg p-4 text-left transition-all">
              <p className="font-semibold text-[#0F172A]">Proprietario</p>
              <p className="text-sm text-slate-600">Pubblica una richiesta di ristrutturazione</p>
            </button>
            <button type="button" data-testid="select-impresa-btn" onClick={() => setStep('impresa')} className="w-full border-2 border-slate-200 hover:border-[#EA580C] rounded-lg p-4 text-left transition-all">
              <p className="font-semibold text-[#0F172A]">Impresa</p>
              <p className="text-sm text-slate-600">Acquista contatti di clienti che cercano imprese</p>
            </button>
            <p className="text-center text-sm text-slate-600">
              Hai già un account?{' '}
              <button type="button" data-testid="toggle-auth-mode-btn" onClick={() => setIsLogin(true)} className="text-[#EA580C] font-medium hover:underline">Accedi</button>
            </p>
          </div>
        )}

        {step !== 'otp' && !isLogin && step === 'proprietario' && (
          <form onSubmit={handleRegisterProp} className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input data-testid="register-prop-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-lg" />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input data-testid="register-prop-cognome-input" value={cognome} onChange={(e) => setCognome(e.target.value)} required className="rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input data-testid="register-prop-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="register-prop-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="register-prop-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="register-prop-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg py-3">
              {loading ? 'Invio...' : 'Crea account'}
            </Button>
            <button type="button" onClick={() => setStep('role')} className="w-full text-sm text-slate-500 hover:text-[#EA580C]">← Indietro</button>
          </form>
        )}

        {step !== 'otp' && !isLogin && step === 'impresa' && (
          <form onSubmit={handleRegisterImpresa} className="space-y-3 mt-4">
            <div>
              <Label>Ragione Sociale</Label>
              <Input data-testid="register-imp-ragione-input" value={ragioneSociale} onChange={(e) => setRagioneSociale(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Referente Impresa</Label>
              <Input data-testid="register-imp-referente-input" value={referente} onChange={(e) => setReferente(e.target.value)} required className="rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Partita IVA</Label>
                <Input data-testid="register-imp-piva-input" value={partitaIva} onChange={(e) => setPartitaIva(e.target.value)} required className="rounded-lg" />
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input data-testid="register-imp-cf-input" value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value)} required className="rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Sede Legale</Label>
              <Input data-testid="register-imp-sede-input" value={sedeLegale} onChange={(e) => setSedeLegale(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input data-testid="register-imp-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="register-imp-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="register-imp-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="register-imp-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg py-3">
              {loading ? 'Invio...' : 'Crea account'}
            </Button>
            <button type="button" onClick={() => setStep('role')} className="w-full text-sm text-slate-500 hover:text-[#EA580C]">← Indietro</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SupportModal = ({ open, onClose }) => {
  const [role, setRole] = useState(''); // proprietario | impresa
  const [submitting, setSubmitting] = useState(false);
  // shared
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [messaggio, setMessaggio] = useState('');
  // proprietario
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  // impresa
  const [ragioneSociale, setRagioneSociale] = useState('');
  const [referente, setReferente] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [sedeLegale, setSedeLegale] = useState('');

  const resetAll = () => {
    setRole(''); setEmail(''); setPhone(''); setOggetto(''); setMessaggio('');
    setNome(''); setCognome('');
    setRagioneSociale(''); setReferente(''); setPartitaIva(''); setCodiceFiscale(''); setSedeLegale('');
  };

  React.useEffect(() => { if (!open) resetAll(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let payload, url;
      if (role === 'proprietario') {
        payload = { nome, cognome, email, phone, oggetto, messaggio };
        url = `${API}/support/proprietario`;
      } else {
        payload = {
          ragione_sociale: ragioneSociale, referente, partita_iva: partitaIva,
          codice_fiscale: codiceFiscale, sede_legale: sedeLegale, email, phone, oggetto, messaggio,
        };
        url = `${API}/support/impresa`;
      }
      const { data } = await axios.post(url, payload, { withCredentials: true });
      toast.success(`Richiesta inviata. Riferimento: ${data.ticket_number}`);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Errore invio richiesta';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="support-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A] flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-[#EA580C]" /> Richiesta di supporto
          </DialogTitle>
          <DialogDescription>Compila i tuoi dati e ti risponderemo al più presto.</DialogDescription>
        </DialogHeader>

        {!role && (
          <div className="space-y-3 mt-4">
            <p className="text-sm text-slate-600">Per chi è la richiesta?</p>
            <button type="button" data-testid="support-role-proprietario" onClick={() => setRole('proprietario')} className="w-full border-2 border-slate-200 hover:border-[#EA580C] rounded-lg p-4 text-left transition-all">
              <p className="font-semibold text-[#0F172A]">Sono un Proprietario</p>
              <p className="text-xs text-slate-600">o vorrei pubblicare una richiesta</p>
            </button>
            <button type="button" data-testid="support-role-impresa" onClick={() => setRole('impresa')} className="w-full border-2 border-slate-200 hover:border-[#EA580C] rounded-lg p-4 text-left transition-all">
              <p className="font-semibold text-[#0F172A]">Sono un'Impresa</p>
              <p className="text-xs text-slate-600">o vorrei iscriverla alla piattaforma</p>
            </button>
          </div>
        )}

        {role === 'proprietario' && (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="support-form-proprietario">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input data-testid="support-nome" value={nome} onChange={(e) => setNome(e.target.value)} required className="rounded-lg" /></div>
              <div><Label>Cognome</Label><Input data-testid="support-cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} required className="rounded-lg" /></div>
            </div>
            <div><Label>Email</Label><Input data-testid="support-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Telefono</Label><Input data-testid="support-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Oggetto</Label><Input data-testid="support-oggetto" value={oggetto} onChange={(e) => setOggetto(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Messaggio</Label><Textarea data-testid="support-messaggio" value={messaggio} onChange={(e) => setMessaggio(e.target.value)} required rows={4} className="rounded-lg" /></div>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setRole('')} variant="outline" className="flex-1">← Indietro</Button>
              <Button type="submit" data-testid="support-submit-prop" disabled={submitting} className="flex-1 bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
                {submitting ? 'Invio...' : 'Invia richiesta'}
              </Button>
            </div>
          </form>
        )}

        {role === 'impresa' && (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="support-form-impresa">
            <div><Label>Ragione Sociale</Label><Input data-testid="support-ragione" value={ragioneSociale} onChange={(e) => setRagioneSociale(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Referente</Label><Input data-testid="support-referente" value={referente} onChange={(e) => setReferente(e.target.value)} required className="rounded-lg" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Partita IVA</Label><Input data-testid="support-piva" value={partitaIva} onChange={(e) => setPartitaIva(e.target.value)} required className="rounded-lg" /></div>
              <div><Label>Codice Fiscale</Label><Input data-testid="support-cf" value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value)} required className="rounded-lg" /></div>
            </div>
            <div><Label>Sede Legale</Label><Input data-testid="support-sede" value={sedeLegale} onChange={(e) => setSedeLegale(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Email</Label><Input data-testid="support-imp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Telefono</Label><Input data-testid="support-imp-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Oggetto</Label><Input data-testid="support-imp-oggetto" value={oggetto} onChange={(e) => setOggetto(e.target.value)} required className="rounded-lg" /></div>
            <div><Label>Messaggio</Label><Textarea data-testid="support-imp-messaggio" value={messaggio} onChange={(e) => setMessaggio(e.target.value)} required rows={4} className="rounded-lg" /></div>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setRole('')} variant="outline" className="flex-1">← Indietro</Button>
              <Button type="submit" data-testid="support-submit-imp" disabled={submitting} className="flex-1 bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
                {submitting ? 'Invio...' : 'Invia richiesta'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Landing;
